// src/pages/healthcare/consultation/consultationComponents/RecordCreationHandler.tsx
// FIXED VERSION - Proper record type detection and initialization

import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Spin, message, Tabs, Alert, Space, Typography, Tag } from "antd";
import { SaveOutlined, CloseOutlined } from "@ant-design/icons";

// API
import { createMedicalHistory } from "../../../../api/healthcare/PatientApi";
import { useMedicalHistoryGenerator, useMedicationGenerator, useTestOrderGenerator } from "../../../../hooks/consult/useLlmRecomendations";
import { createTest } from "../../../../api/healthcare/Testing/TestApi";

// Types
import { Transcript } from "../../../../types/appointments/ConsultTypes";
import { Suggestion, MedicalHistoryType, TestType } from "../../../../utils/healthcare/SugestionUtils";
import { User } from "../../../../types/admin/UserTypes";
import { Test } from "../../../../types/healthcare/TestTypes";
import { MedicalHistory } from "../../../../types/healthcare/MedicalHistoryTypes";
import { Patient } from "../../../../types/healthcare/PatientTypes";

// Components
import CreateMedicalHistoryModal from "../../patient/modalComponents/submodals/CreateMedicalHistoryModal";
import CreateMedicationModal from "../../patient/modalComponents/submodals/CreateMedicationModal";
import CreateTestModal from "../../results/CreateTestOrderModal";
import type { UserUuid } from "../../../../types/identity";

const { TabPane } = Tabs;
const { Text } = Typography;

interface RecordCreationHandlerProps {
	isOpen: boolean;
	onClose: () => void;
	patient: Patient;
	suggestion: Suggestion;
	transcripts: Transcript[];
	currentUser: User;
	onRecordCreated: (record: any, recordType: "medical_history" | "medication" | "test_order") => void;
}

/**
 * Determine the correct record type based on suggestion flags
 * Priority: medication > testing > medical_history
 * This ensures specific actions (meds, tests) take precedence over general history
 */
function determineRecordType(suggestion: Suggestion): "medical_history" | "medication" | "test_order" | null {
	// Check for medication first (most specific)
	if (suggestion.medication) {
		return "medication";
	}

	// Check for testing
	if (suggestion.testing) {
		return "test_order";
	}

	// Check for medical history last
	if (suggestion.medicalHistoryRecomended && suggestion.medical_history_record_type) {
		return "medical_history";
	}

	return null;
}

/**
 * Create proper default medication object
 */
function getDefaultMedication(patientId: UserUuid, userId: UserUuid) {
	return {
		id: 0,
		patient_id: patientId,
		medication_name: "",
		dosage: "",
		frequency: "once daily",
		instructions: "",
		start_date: new Date().toISOString(),
		end_date: null,
		prescribed_by: userId,
		active: true,
		created_at: new Date().toISOString(),
		updated_at: new Date().toISOString(),
		details: {},
	};
}

/**
 * Create proper default test order object
 */
function getDefaultTestOrder(patientId: UserUuid, userId: UserUuid, userFirstName: string, userLastName: string, testType: string) {
	return {
		id: 0,
		patient_id: patientId,
		test_type: testType || "laboratory",
		test_category: testType || "laboratory",
		test_name: "",
		ordering_provider_id: userId,
		ordering_provider: {
			id: userId,
			first_name: userFirstName,
			last_name: userLastName,
		},
		facility: "",
		priority: "routine",
		status: "ordered",
		reason: "",
		billable_diagnosis: "",
		details: {},
		test_date: null,
		scheduled_date: null,
		completed_date: null,
		results_available_date: null,
		results_reviewed_date: null,
		results: null,
		result_notes: null,
		result_documents: [],
		result_provider_id: null,
		result_provider: null,
		is_abnormal: false,
		requires_followup: false,
		followup_notes: null,
	};
}

const RecordCreationHandler: React.FC<RecordCreationHandlerProps> = ({ isOpen, onClose, patient, suggestion, transcripts, currentUser, onRecordCreated }) => {
	// Determine record type using improved logic
	const recordType = determineRecordType(suggestion);

	const [editedRecord, setEditedRecord] = useState<any>(null);
	const [activeTab, setActiveTab] = useState("1");
	const [validationErrors, setValidationErrors] = useState<string[]>([]);

	const isMountedRef = useRef(true);
	const recordCreationInitiatedRef = useRef(false);
	const currentOperationRef = useRef<string | null>(null);

	// Hooks for generation
	const {
		generateRecord: generateMedicalHistory,
		isGenerating: isGeneratingMedicalHistory,
		generatedRecord: generatedMedicalHistory,
		error: medicalHistoryError,
	} = useMedicalHistoryGenerator(patient, suggestion, suggestion.medical_history_record_type as MedicalHistoryType, transcripts, currentUser.id, currentUser.first_name, currentUser.last_name);

	const { generateRecord: generateMedication, isGenerating: isGeneratingMedication, generatedRecord: generatedMedication, error: medicationError } = useMedicationGenerator(patient, suggestion, transcripts, currentUser.id);

	const {
		generateRecord: generateTest,
		isGenerating: isGeneratingTest,
		generatedRecord: generatedTest,
		error: TestError,
	} = useTestOrderGenerator(patient, suggestion, (suggestion.test_type as TestType) || "laboratory", transcripts, currentUser.id, currentUser.first_name, currentUser.last_name);

	const isGenerating = isGeneratingMedicalHistory || isGeneratingMedication || isGeneratingTest;
	const error = medicalHistoryError || medicationError || TestError;

	// Initialize default record structure based on type
	useEffect(() => {
		isMountedRef.current = true;
		recordCreationInitiatedRef.current = false;
		currentOperationRef.current = null;

		console.log(`Initializing record type: ${recordType}`);

		// Set appropriate default based on record type
		if (recordType === "medical_history") {
			setEditedRecord({});
		} else if (recordType === "medication") {
			setEditedRecord(getDefaultMedication(patient.id, currentUser.id));
		} else if (recordType === "test_order") {
			setEditedRecord(getDefaultTestOrder(patient.id, currentUser.id, currentUser.first_name, currentUser.last_name, suggestion.test_type || "laboratory"));
		}

		return () => {
			isMountedRef.current = false;
		};
	}, [recordType, patient.id, currentUser, suggestion.test_type]);

	// Update when medical history is generated
	useEffect(() => {
		if (generatedMedicalHistory && isMountedRef.current && recordType === "medical_history") {
			console.log("Setting generated medical history record:", generatedMedicalHistory);
			setEditedRecord(generatedMedicalHistory);
		}
	}, [generatedMedicalHistory, recordType]);

	// Update when medication is generated
	useEffect(() => {
		if (generatedMedication && isMountedRef.current && recordType === "medication") {
			console.log("Setting generated medication record:", generatedMedication);
			setEditedRecord(generatedMedication);
		}
	}, [generatedMedication, recordType]);

	// Update when test order is generated
	useEffect(() => {
		if (generatedTest && isMountedRef.current && recordType === "test_order") {
			console.log("Setting generated test order record:", generatedTest);
			setEditedRecord(generatedTest);
		}
	}, [generatedTest, recordType]);

	// Trigger generation when modal opens
	useEffect(() => {
		if (isOpen && !isGenerating && !recordCreationInitiatedRef.current) {
			recordCreationInitiatedRef.current = true;

			const operationId = `generate_${Date.now()}_${Math.random().toString(36).substring(2)}`;
			currentOperationRef.current = operationId;

			console.log(`Generating ${recordType} record...`);

			if (recordType === "medical_history") {
				console.log("Calling generateMedicalHistory");
				generateMedicalHistory();
			} else if (recordType === "medication") {
				console.log("Calling generateMedication");
				generateMedication();
			} else if (recordType === "test_order") {
				console.log("Calling generateTest");
				generateTest();
			}
		}
	}, [isOpen, recordType, isGenerating, generateMedicalHistory, generateMedication, generateTest]);

	// Reset when modal closes
	useEffect(() => {
		if (!isOpen && isMountedRef.current) {
			setEditedRecord(null);
			setValidationErrors([]);
			recordCreationInitiatedRef.current = false;
			currentOperationRef.current = null;
		}
	}, [isOpen]);

	const handleSave = async () => {
		if (currentOperationRef.current === "saving") return;

		currentOperationRef.current = "saving";

		try {
			let newRecord;

			if (recordType === "medical_history") {
				console.log("Saving medical history record:", editedRecord);
				const payload = {
					...editedRecord,
					details: typeof editedRecord.details === "object" ? JSON.stringify(editedRecord.details) : editedRecord.details,
				} as unknown as Partial<MedicalHistory>;
				newRecord = await createMedicalHistory(payload);
			} else if (recordType === "test_order") {
				console.log("Saving test order:", editedRecord);
				const payload = {
					...editedRecord,
					patient_id: patient.id,
					details: typeof editedRecord.details === "object" ? JSON.stringify(editedRecord.details) : editedRecord.details,
					status: editedRecord.status || "ordered",
				} as unknown as Partial<Test>;
				newRecord = await createTest(payload);
			} else if (recordType === "medication") {
				console.log("Saving medication record:", editedRecord);
				// TODO: Call medication API when available
				newRecord = editedRecord;
			}

			onRecordCreated(newRecord, recordType as "medical_history" | "medication" | "test_order");

			message.success(`${recordType?.replace("_", " ")} record created successfully`);
			onClose();
		} catch (error) {
			console.error("Error saving record:", error);
			message.error(`Failed to save ${recordType?.replace("_", " ")} record`);
		} finally {
			currentOperationRef.current = null;
		}
	};

	const renderSuggestionCard = () => {
		return (
			<div
				style={{
					padding: 16,
					background: "var(--background-color)",
					borderRadius: 8,
					border: "1px solid var(--border-color)",
				}}
			>
				<Text strong style={{ fontSize: 14, display: "block", marginBottom: 8 }}>
					Based on this suggestion
				</Text>
				<Text style={{ fontSize: 13, display: "block", marginBottom: 8 }}>{suggestion.text}</Text>
				<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 12 }}>
					Why: {suggestion.why}
				</Text>
				<Space wrap>
					<Tag color="blue">Type: {recordType}</Tag>
					<Tag color="blue">Confidence: {suggestion.confidence.toFixed(2)}</Tag>
					<Tag color={suggestion.urgency === "critical" ? "error" : suggestion.urgency === "high" ? "warning" : "default"}>Urgency: {suggestion.urgency}</Tag>
					<Tag color="success">Evidence: {suggestion.evidence_level}</Tag>
				</Space>
			</div>
		);
	};

	const renderRecordForm = () => {
		// Don't render form until record is generated
		if (!editedRecord) {
			return (
				<div style={{ padding: 48, textAlign: "center" }}>
					<Spin size="large" />
					<Text style={{ display: "block", marginTop: 16 }}>Preparing form...</Text>
				</div>
			);
		}

		console.log(`Rendering form for ${recordType}. Current record:`, editedRecord);

		switch (recordType) {
			case "medical_history":
				return <CreateMedicalHistoryModal currentRecord={editedRecord} isEditing={false} setCurrentRecord={setEditedRecord} />;

			case "medication":
				return <CreateMedicationModal isVisible={true} onClose={() => {}} currentMedication={editedRecord} isEditing={false} setCurrentMedication={setEditedRecord} isConsult={true} />;

			case "test_order":
				return <CreateTestModal isOpen={true} onClose={() => {}} onSave={() => {}} currentOrder={editedRecord} setCurrentOrder={setEditedRecord} />;

			default:
				return <Alert message="Unknown record type" description={`Unable to create form for record type: ${recordType}`} type="warning" showIcon />;
		}
	};

	const getDialogTitle = () => {
		switch (recordType) {
			case "medical_history":
				return "Create Medical History Record";
			case "medication":
				return "Create Medication Record";
			case "test_order":
				return "Create Test Order";
			default:
				return "Create Record";
		}
	};

	return (
		<Modal
			title={getDialogTitle()}
			open={isOpen}
			onCancel={onClose}
			width="80vw"
			style={{ maxWidth: 1000 }}
			footer={[
				<Button key="cancel" icon={<CloseOutlined />} onClick={onClose}>
					Cancel
				</Button>,
				<Button key="save" type="primary" icon={<SaveOutlined />} onClick={handleSave} disabled={isGenerating || !editedRecord || validationErrors.length > 0}>
					Save
				</Button>,
			]}
			closable={false}
			maskClosable={false}
		>
			{isGenerating ? (
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						padding: 48,
					}}
				>
					<Spin size="large" />
					<Text style={{ marginTop: 16 }}>Generating {recordType && recordType.replace("_", " ")} record via server...</Text>
					<Text type="secondary" style={{ marginTop: 8, fontSize: 12 }}>
						This may take a few seconds
					</Text>
				</div>
			) : error ? (
				<Alert
					message="Generation Error"
					description={error}
					type="error"
					showIcon
					action={
						<Button
							size="small"
							danger
							onClick={() => {
								if (recordType === "medical_history") generateMedicalHistory();
								else if (recordType === "medication") generateMedication();
								else if (recordType === "test_order") generateTest();
							}}
						>
							Retry
						</Button>
					}
				/>
			) : (
				<Tabs activeKey={activeTab} onChange={setActiveTab}>
					<TabPane tab="Record Details" key="1">
						{recordType ? renderRecordForm() : <Alert message="No record type specified" description="This suggestion does not recommend creating a record." type="warning" showIcon />}
					</TabPane>
					<TabPane tab="Source Suggestion" key="2">
						{renderSuggestionCard()}
					</TabPane>
				</Tabs>
			)}
		</Modal>
	);
};

export default RecordCreationHandler;
